import React, { useCallback, useState } from 'react';
import { Panel} from 'rsuite';
import { isMobile } from 'react-device-detect';
import { Link } from 'react-router-dom';

import RulesSidebarNav from './RulesSidebarNav';
import NavigationBar from '../Shared/NavigationBar';
import RulesDisclaimer from '../Shared/RulesDisclaimer';
import useScrollToTop from '../../helpers/useScrollToTop';
import { usePreferences } from '../Preferences/PreferencesContext';
import { getThemeColors } from '../../helpers/themeColors';

const SPACING = {
  containerMaxWidth: 1100,
  containerPadding: 24,
  containerPaddingMobile: 12,
  headerMarginTop: 24,
  headerMarginBottom: 24,
  titleMarginBottom: 8,
  sectionMarginBottom: 32,
  panelMarginBottom: 16,
  sidebarWidth: 220,
  sidebarGap: 32,
  sidebarPadding: 16,
  sidebarBorderRadius: 6,
  sidebarTitleMarginBottom: 8,
  sidebarItemPadding: '6px 0',
  stickyTop: 20,
  backLinkMarginBottom: 16,
  backLinkPadding: '10px 0',
  introPadding: 20,
  introBorderRadius: 8,
  paragraphMarginBottom: 12,
  diagramPadding: 24,
  diagramBorderRadius: 8,
  diagramItemPadding: '10px 16px',
  diagramItemBorderRadius: 6,
  diagramItemMarginBottom: 4,
  diagramSubItemPaddingLeft: 24,
  diagramSubItemPaddingLeftMobile: 16,
  phaseHeaderPadding: '8px 12px',
  phaseHeaderBorderRadius: 6,
  phaseHeaderMarginBottom: 12,
  stepPadding: 16,
  stepBorderRadius: 6,
  stepMarginBottom: 12,
  priorityBadgePadding: '2px 8px',
  priorityBadgeBorderRadius: 4,
  keyRulePadding: '6px 0',
  keyRuleBulletMarginRight: 8,
  extraTopicPadding: 20,
  extraTopicBorderRadius: 8,
  extraTopicMarginBottom: 16,
  cardLinkPadding: '1px 4px',
  cardLinkBorderRadius: 3,
};

const FONT = {
  title: 28,
  titleMobile: 22,
  subtitle: 15,
  subtitleMobile: 14,
  sidebarTitle: 14,
  sidebarTitleWeight: 600,
  sidebarTitleLetterSpacing: 1,
  sidebarItem: 14,
  sectionHeading: 22,
  sectionHeadingWeight: 600,
  phaseHeading: 16,
  phaseHeadingWeight: 700,
  stepName: 16,
  stepNameWeight: 600,
  stepDescription: 14,
  stepDescriptionLineHeight: 1.7,
  keyRuleText: 14,
  keyRuleLineHeight: 1.6,
  introText: 14,
  introTextLineHeight: 1.7,
  diagramItem: 14,
  diagramItemWeight: 600,
  diagramSubItem: 14,
  diagramArrow: 16,
  priorityBadge: 14,
  priorityBadgeWeight: 600,
  backLink: 14,
  cardLink: 14,
  extraHeading: 18,
  extraHeadingWeight: 600,
  extraText: 14,
  extraTextLineHeight: 1.7,
};


const PRIORITY_YES = 'priority';
const PRIORITY_NO = 'no-priority';
const PRIORITY_SPECIAL = 'conditional';

const CARD_NAME_REGEX = /\b(Monastery Swiftspear|Teferi, Time Raveler|Wilderness Reclamation|Leyline of Anticipation|Vedalken Orrery|Nexus of Fate|Time Warp|Aurelia, the Warleader|Godo, Bandit Warlord|Savage Beating)\b/g;

const renderTextWithCardLinks = (text, colors) => {
  const parts = [];
  let lastIndex = 0;
  let match;

  const regex = new RegExp(CARD_NAME_REGEX.source, 'g');
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const cardName = match[0];
    parts.push(
      <Link
        key={`${cardName}-${match.index}`}
        to={`/cardsearch/${encodeURIComponent(cardName)}`}
        style={{
          color: colors.accentPrimary,
          backgroundColor: colors.accentBg,
          padding: SPACING.cardLinkPadding,
          borderRadius: SPACING.cardLinkBorderRadius,
          fontSize: FONT.cardLink,
          textDecoration: 'none',
        }}
      >
        {cardName}
      </Link>
    );
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : text;
};

const TURN_PHASES = [
  {
    id: 'beginning-phase',
    name: 'Beginning Phase',
    steps: [
      {
        id: 'untap',
        name: 'Untap Step',
        priority: PRIORITY_NO,
        description: 'The active player untaps all their permanents simultaneously. Phasing also happens during this step. Phased-out permanents phase in, and permanents with phasing phase out.',
        keyRules: [
          'No player receives priority during the untap step.',
          'No spells can be cast and no abilities can be activated.',
          '"At the beginning of your untap step" triggered abilities still trigger but wait to be put on the stack until a player receives priority (in the upkeep step).',
          'The active player chooses the order in which their permanents untap (relevant for cards like Mesmeric Orb).',
        ],
      },
      {
        id: 'upkeep',
        name: 'Upkeep Step',
        priority: PRIORITY_YES,
        description: '"At the beginning of your upkeep" triggered abilities trigger and are placed on the stack. The active player then receives priority.',
        keyRules: [
          'Many cards have upkeep triggers: costs to pay, effects to resolve, or penalties for not acting.',
          'The active player can cast instants and activate abilities during upkeep before moving to the draw step.',
          'This is often the last chance to act before the opponent draws a card.',
        ],
      },
      {
        id: 'draw',
        name: 'Draw Step',
        priority: PRIORITY_YES,
        description: 'The active player draws a card as a turn-based action (this does not use the stack). Then "at the beginning of your draw step" triggered abilities trigger. The active player receives priority.',
        keyRules: [
          'The first player to take a turn in a game skips their first draw step.',
          'The draw itself cannot be responded to. It is a turn-based action.',
          'Triggered abilities from drawing (e.g., from Monastery Swiftspear\'s prowess if a spell is cast here) do use the stack.',
        ],
      },
    ],
  },
  {
    id: 'precombat-main',
    name: 'Pre-Combat Main Phase',
    steps: [
      {
        id: 'main-1',
        name: 'First Main Phase',
        priority: PRIORITY_YES,
        description: 'The active player can play a land (once per turn, as a special action that doesn\'t use the stack), cast spells of any type (creatures, sorceries, instants, artifacts, enchantments, planeswalkers, battles), and activate abilities.',
        keyRules: [
          'Sorceries and other sorcery-speed spells can only be cast during your main phase when the stack is empty.',
          'Playing a land is a special action. It does not use the stack and cannot be responded to.',
          'You can play your land before or after casting spells in the same main phase.',
          'Consider whether to play your land before or after combat for strategic advantage.',
        ],
      },
    ],
  },
  {
    id: 'combat-phase',
    name: 'Combat Phase',
    linkTo: '/rules/combat',
    steps: [
      { id: 'beginning-of-combat', name: 'Beginning of Combat', priority: PRIORITY_YES, description: '"At the beginning of combat" abilities trigger. Both players get priority. Last chance to tap potential attackers.' },
      { id: 'declare-attackers', name: 'Declare Attackers', priority: PRIORITY_YES, description: 'The active player declares attackers (tapping them unless they have vigilance). "Whenever this creature attacks" triggers fire. Priority is given.' },
      { id: 'declare-blockers', name: 'Declare Blockers', priority: PRIORITY_YES, description: 'The defending player declares blockers. Attacker orders multiple blockers for damage assignment. Priority is given.' },
      { id: 'combat-damage', name: 'Combat Damage', priority: PRIORITY_YES, description: 'All creatures assign and deal combat damage simultaneously (first strike/double strike creates an additional sub-step). Priority is given after damage is dealt.' },
      { id: 'end-of-combat', name: 'End of Combat', priority: PRIORITY_YES, description: '"At end of combat" triggers fire. Creatures are still attacking/blocking during this step. After it ends, creatures leave combat.' },
    ],
  },
  {
    id: 'postcombat-main',
    name: 'Post-Combat Main Phase',
    steps: [
      {
        id: 'main-2',
        name: 'Second Main Phase',
        priority: PRIORITY_YES,
        description: 'Functions identically to the first main phase. The active player can play a land (if they haven\'t already this turn), cast spells, and activate abilities.',
        keyRules: [
          'Playing creatures after combat can be strategically better, as you have more information and your opponent may have used removal during combat.',
          'If you have not played a land yet, you may do so during this phase.',
          'This is the last main phase. Sorcery-speed actions cannot be taken after this.',
        ],
      },
    ],
  },
  {
    id: 'ending-phase',
    name: 'Ending Phase',
    steps: [
      {
        id: 'end-step',
        name: 'End Step',
        priority: PRIORITY_YES,
        description: '"At the beginning of the end step" and "at the beginning of the next end step" triggered abilities trigger. The active player receives priority.',
        keyRules: [
          'Many delayed triggered abilities fire here ("at the beginning of the next end step").',
          'This is the last chance for either player to cast instants or activate abilities during the turn (before cleanup).',
          '"Until end of turn" effects are still active during the end step. They end during cleanup.',
        ],
      },
      {
        id: 'cleanup',
        name: 'Cleanup Step',
        priority: PRIORITY_SPECIAL,
        description: 'The active player discards down to their maximum hand size (usually 7). Then all damage is removed from permanents and "until end of turn" and "this turn" effects end. These happen simultaneously as turn-based actions.',
        keyRules: [
          'Normally, no player receives priority during cleanup.',
          'However, if a state-based action is performed or a triggered ability triggers during cleanup, players DO receive priority and a new cleanup step occurs afterward.',
          'Example: discarding a card with madness triggers an ability, giving players priority in an additional cleanup step.',
          '"Until end of turn" effects end here, not during the end step.',
        ],
      },
    ],
  },
];

const EXTRA_TOPICS = [
  {
    id: 'extra-combat',
    title: 'Extra Combat Phases',
    content:
      'Some cards grant additional combat phases within a single turn. When this happens, the turn gains an additional combat phase (with all five steps) and usually an additional main phase after it.\n\n' +
      'Cards like Aurelia, the Warleader, Godo, Bandit Warlord, and Savage Beating can create extra combat phases. Each extra combat phase is a full combat phase with its own Beginning of Combat, Declare Attackers, Declare Blockers, Combat Damage, and End of Combat steps.\n\n' +
      'Creatures that attacked in the first combat phase are tapped (unless they have vigilance) and cannot attack again unless they are untapped by an effect.',
  },
  {
    id: 'extra-turns',
    title: 'Extra Turns',
    content:
      'Some cards grant extra turns (e.g., Nexus of Fate, Time Warp). An extra turn is inserted directly after the current turn. If multiple extra turns are created, they are taken in LIFO order (last created, first taken).\n\n' +
      'Extra turns are complete turns with all phases and steps. The player who takes the extra turn is the active player for that turn.\n\n' +
      'Note: the first draw step skip only applies to the very first turn of the game. Extra turns always include a draw step.',
  },
  {
    id: 'skipping',
    title: 'Skipping Phases and Steps',
    content:
      'Some effects skip phases or steps (e.g., "Skip your draw step" or "Skip your next combat phase"). When a phase or step is skipped, nothing that would happen during that phase or step occurs. Triggered abilities that would trigger "at the beginning of" a skipped step never trigger.\n\n' +
      'If a turn has no combat phase (because it was skipped or the active player chose not to enter combat in certain formats), the turn goes directly from the pre-combat main phase to the post-combat main phase.',
  },
];

const SIDEBAR_ITEMS = [
  { id: 'intro', label: 'Introduction' },
  { id: 'timeline', label: 'Turn Flow' },
  ...TURN_PHASES.map((phase) => ({
    id: phase.id,
    label: phase.name,
  })),
  { id: 'extra', label: 'Extra Phases & Turns' },
];

const getPriorityBadge = (priority) => {
  if (priority === PRIORITY_YES) {
    return { text: '✓ Priority', bg: '#1b4332', color: '#52b788' };
  }
  if (priority === PRIORITY_NO) {
    return { text: '✗ No Priority', bg: '#4a1a1a', color: '#e07070' };
  }
  return { text: '⚠ Conditional', bg: '#4a3a1a', color: '#e0c060' };
};

const TurnStructure = () => {
  const { preferences } = usePreferences();
  const t = getThemeColors(preferences.theme);

  const [activeId, setActiveId] = useState('intro');

  useScrollToTop();

  const scrollToSection = useCallback((sectionId) => {
    setActiveId(sectionId);
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [setActiveId]);

  return (
    <>
      <NavigationBar />
      <div
        style={{
          maxWidth: SPACING.containerMaxWidth,
          margin: '0 auto',
          padding: isMobile ? SPACING.containerPaddingMobile : SPACING.containerPadding,
        }}
      >
        {/* Back link */}
        <div style={{ marginBottom: SPACING.backLinkMarginBottom }}>
          <Link to="/rules" style={{ color: t.accentPrimary, fontSize: FONT.backLink, textDecoration: 'none', display: 'inline-block', padding: SPACING.backLinkPadding }}>
            ← Back to Rules Reference
          </Link>
        </div>


        {/* Header */}
        <div style={{ marginTop: SPACING.headerMarginTop, marginBottom: SPACING.headerMarginBottom }}>
          <h1 style={{ fontSize: isMobile ? FONT.titleMobile : FONT.title, color: t.textPrimary, marginBottom: SPACING.titleMarginBottom }}>
            🔄 Turn Structure
          </h1>
          <div style={{ fontSize: isMobile ? FONT.subtitleMobile : FONT.subtitle, color: t.textMuted }}>
            Every phase and step of a Magic turn, from untap through cleanup
          </div>
        </div>

        {/* Layout: sidebar + content */}
        <div style={{ display: 'flex', gap: isMobile ? 0 : SPACING.sidebarGap, alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>
          {/* Sidebar Navigation - responsive layout handled by component */}
          <RulesSidebarNav
            sections={SIDEBAR_ITEMS}
            activeId={activeId}
            onSelect={scrollToSection}
          />

          {/* Main content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Introduction */}
            <div id="intro" style={{ marginBottom: SPACING.sectionMarginBottom }}>
              <div
                style={{
                  backgroundColor: t.backgroundSecondary,
                  border: `1px solid ${t.borderPrimary}`,
                  borderRadius: SPACING.introBorderRadius,
                  padding: SPACING.introPadding,
                }}
              >
                <div style={{ fontSize: FONT.introText, color: t.textSecondary, lineHeight: FONT.introTextLineHeight }}>
                  <p style={{ marginBottom: SPACING.paragraphMarginBottom }}>
                    A <strong style={{ color: t.textPrimary }}>turn</strong> in Magic: The Gathering is divided into
                    phases, and phases are divided into steps. Understanding when each phase and step occurs, and
                    whether players receive <strong style={{ color: t.textPrimary }}>priority</strong>, is essential
                    for competitive play.
                  </p>
                  <p style={{ marginBottom: SPACING.paragraphMarginBottom }}>
                    Priority determines when you can cast spells and activate abilities. Most steps grant priority to
                    the active player, but two notable exceptions are the <strong style={{ color: '#e07070' }}>Untap Step</strong> and
                    the <strong style={{ color: '#e0c060' }}>Cleanup Step</strong> (which only grants
                    priority conditionally).
                  </p>
                  <p style={{ margin: 0 }}>
                    The turn flows: <strong style={{ color: t.textPrimary }}>Beginning Phase</strong> → <strong style={{ color: t.textPrimary }}>Pre-Combat Main Phase</strong> → <strong style={{ color: t.textPrimary }}>Combat Phase</strong> → <strong style={{ color: t.textPrimary }}>Post-Combat Main Phase</strong> → <strong style={{ color: t.textPrimary }}>Ending Phase</strong>.
                  </p>
                </div>
              </div>
            </div>

            {/* Turn Flow Timeline */}
            <div id="timeline" style={{ marginBottom: SPACING.sectionMarginBottom }}>
              <h2 style={{ fontSize: FONT.sectionHeading, fontWeight: FONT.sectionHeadingWeight, color: t.textPrimary, marginBottom: SPACING.panelMarginBottom }}>
                Turn Flow
              </h2>
              <div
                style={{
                  backgroundColor: t.cardBackground,
                  border: `1px solid ${t.borderPrimary}`,
                  borderRadius: SPACING.diagramBorderRadius,
                  padding: SPACING.diagramPadding,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                {TURN_PHASES.map((phase, pIdx) => (
                  <React.Fragment key={phase.id}>
                    {/* Phase header */}
                    <div
                      onClick={() => scrollToSection(phase.id)}
                      style={{
                        backgroundColor: t.accentBg,
                        border: `1px solid ${t.accentSecondary}`,
                        borderRadius: SPACING.diagramItemBorderRadius,
                        padding: SPACING.diagramItemPadding,
                        width: '100%',
                        maxWidth: 400,
                        textAlign: 'center',
                        cursor: 'pointer',
                        marginBottom: SPACING.diagramItemMarginBottom,
                      }}
                    >
                      <span style={{ fontSize: FONT.diagramItem, fontWeight: FONT.diagramItemWeight, color: t.textPrimary }}>
                        {phase.name}
                      </span>
                    </div>
                    {/* Sub-steps */}
                    {phase.steps.map((step) => {
                      const badge = getPriorityBadge(step.priority);
                      return (
                        <div
                          key={step.id}
                          style={{
                            backgroundColor: step.priority === PRIORITY_NO ? '#4a1a1a' : t.backgroundSecondary,
                            border: `1px solid ${t.borderPrimary}`,
                            borderRadius: SPACING.diagramItemBorderRadius,
                            padding: SPACING.diagramItemPadding,
                            width: '100%',
                            maxWidth: 360,
                            marginLeft: isMobile ? SPACING.diagramSubItemPaddingLeftMobile : SPACING.diagramSubItemPaddingLeft,
                            marginBottom: SPACING.diagramItemMarginBottom,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <span style={{ fontSize: FONT.diagramSubItem, color: t.textSecondary }}>
                            {step.name}
                          </span>
                          <span
                            style={{
                              fontSize: FONT.priorityBadge,
                              fontWeight: FONT.priorityBadgeWeight,
                              color: badge.color,
                              backgroundColor: badge.bg,
                              padding: SPACING.priorityBadgePadding,
                              borderRadius: SPACING.priorityBadgeBorderRadius,
                              flexShrink: 0,
                            }}
                          >
                            {badge.text}
                          </span>
                        </div>
                      );
                    })}
                    {pIdx < TURN_PHASES.length - 1 && (
                      <div style={{ fontSize: FONT.diagramArrow, color: t.textFaint, marginBottom: SPACING.diagramItemMarginBottom }}>
                        ▼
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Phase Detail Panels */}
            {TURN_PHASES.map((phase) => (
              <div
                key={phase.id}
                id={phase.id}
                style={{ marginBottom: SPACING.sectionMarginBottom }}
              >
                <div
                  style={{
                    backgroundColor: t.accentBg,
                    padding: SPACING.phaseHeaderPadding,
                    borderRadius: SPACING.phaseHeaderBorderRadius,
                    marginBottom: SPACING.phaseHeaderMarginBottom,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <h2 style={{ fontSize: FONT.phaseHeading, fontWeight: FONT.phaseHeadingWeight, color: t.accentPrimary, margin: 0 }}>
                    {phase.name}
                  </h2>
                  {phase.linkTo && (
                    <Link to={phase.linkTo} style={{ color: t.accentPrimary, fontSize: FONT.cardLink, textDecoration: 'none' }}>
                      See detailed breakdown →
                    </Link>
                  )}
                </div>

                {phase.steps.map((step) => {
                  const badge = getPriorityBadge(step.priority);
                  return (
                    <Panel
                      key={step.id}
                      bordered
                      collapsible
                      defaultExpanded
                      header={
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <span style={{ fontSize: FONT.stepName, fontWeight: FONT.stepNameWeight, color: t.textPrimary }}>
                            {step.name}
                          </span>
                          <span
                            style={{
                              fontSize: FONT.priorityBadge,
                              fontWeight: FONT.priorityBadgeWeight,
                              color: badge.color,
                              backgroundColor: badge.bg,
                              padding: SPACING.priorityBadgePadding,
                              borderRadius: SPACING.priorityBadgeBorderRadius,
                            }}
                          >
                            {badge.text}
                          </span>
                        </div>
                      }
                      style={{
                        backgroundColor: step.priority === PRIORITY_NO ? '#4a1a1a' : t.cardBackground,
                        borderColor: t.borderPrimary,
                        marginBottom: SPACING.stepMarginBottom,
                      }}
                    >
                      <div
                        style={{
                          fontSize: FONT.stepDescription,
                          color: t.textSecondary,
                          lineHeight: FONT.stepDescriptionLineHeight,
                          marginBottom: step.keyRules && step.keyRules.length > 0 ? SPACING.panelMarginBottom : 0,
                        }}
                      >
                        {renderTextWithCardLinks(step.description, t)}
                      </div>

                      {step.keyRules && step.keyRules.length > 0 && (
                        <div>
                          {step.keyRules.map((rule, rIdx) => (
                            <div
                              key={`${step.id}-rule-${rIdx}`}
                              style={{
                                display: 'flex',
                                padding: SPACING.keyRulePadding,
                                fontSize: FONT.keyRuleText,
                                color: t.textSecondary,
                                lineHeight: FONT.keyRuleLineHeight,
                              }}
                            >
                              <span style={{ color: t.accentPrimary, marginRight: SPACING.keyRuleBulletMarginRight, flexShrink: 0 }}>•</span>
                              <span>{renderTextWithCardLinks(rule, t)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </Panel>
                  );
                })}
              </div>
            ))}

            {/* Extra Phases and Steps */}
            <div id="extra" style={{ marginBottom: SPACING.sectionMarginBottom }}>
              <h2 style={{ fontSize: FONT.sectionHeading, fontWeight: FONT.sectionHeadingWeight, color: t.textPrimary, marginBottom: SPACING.panelMarginBottom }}>
                Extra Phases & Turns
              </h2>

              {EXTRA_TOPICS.map((topic) => (
                <div
                  key={topic.id}
                  style={{
                    backgroundColor: t.cardBackground,
                    border: `1px solid ${t.borderPrimary}`,
                    borderRadius: SPACING.extraTopicBorderRadius,
                    padding: SPACING.extraTopicPadding,
                    marginBottom: SPACING.extraTopicMarginBottom,
                  }}
                >
                  <h3 style={{ fontSize: FONT.extraHeading, fontWeight: FONT.extraHeadingWeight, color: t.textPrimary, marginBottom: SPACING.phaseHeaderMarginBottom }}>
                    {topic.title}
                  </h3>
                  <div
                    style={{
                      fontSize: FONT.extraText,
                      color: t.textSecondary,
                      lineHeight: FONT.extraTextLineHeight,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {renderTextWithCardLinks(topic.content, t)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <RulesDisclaimer />
      </div>
    </>
  );
};

export default TurnStructure;
